import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FiEdit2, FiLayers, FiPlus, FiTrash2 } from "react-icons/fi";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import { useModal } from "../../../contexts/ModalContext";
import ModalNovaCategoria from "./ModalNovaCategoria";
import ModalEditarCategoria from "./ModalEditarCategoria";
import ModalDeleteCategoria from "./ModalDeleteCategoria";
import ModalCatalogoTipoRisco from "./ModalCatalogoTipoRisco";
import useApi from "../../../hooks/useApi";

const Container = styled.div`
    width: 100%;
    height: min(70dvh, 520px);
    display: flex;
    flex-direction: column;
    gap: 14px;
    box-sizing: border-box;
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    flex-shrink: 0;
`;

const Titulo = styled.h2`
    margin: 0;
    font-size: 18px;
    color: #263b4b;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
        color: #263b4b;
    }
`;

const BotaoNovo = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background-color: #263b4b;
    color: #fff;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.15s ease;

    &:hover {
        background-color: #1e2f3c;
        transform: translateY(-1px);
    }
`;

const TableShell = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
        outline-offset: 2px;
    }
`;

const ModalCatalogoCategRisco = () => {
    const api = useApi();
    const { closeModal, isModalOpen, openModal } = useModal();

    const [loading, setLoading] = useState(true);
    const [categorias, setCategorias] = useState([]);
    const [selectedCategoria, setSelectedCategoria] = useState(null);

    const getData = useCallback(async () => {
        setLoading(true);

        try {
            const { data } = await api.get("/Painel/API/Desvios/Categorias/");
            setCategorias(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setCategorias([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        getData();
    }, [getData]);

    const openEditModal = useCallback(
        (obj) => {
            setSelectedCategoria(obj);
            openModal("modalEditCategoriaRiscoDesvios");
        },
        [openModal]
    );

    const openDeleteModal = useCallback(
        (obj) => {
            setSelectedCategoria(obj);
            openModal("modalDeleteCategoriaRiscoDesvios");
        },
        [openModal]
    );

    const closeDeleteModal = useCallback(() => {
        setSelectedCategoria(null);
        closeModal();
    }, [closeModal]);

    const openTiposRiscoModal = useCallback(
        (obj) => {
            setSelectedCategoria(obj);
            openModal("modalTiposRiscoDesvios");
        },
        [openModal]
    );

    const closeTiposRiscoModal = useCallback(() => {
        setSelectedCategoria(null);
        closeModal();
    }, [closeModal]);

    const columns = useMemo(
        () => [
            {
                id: "nome",
                header: "CATEGORIA",
                accessor: (row) => `${row.nome} (${row.tipos})`,
                sortable: true,
                sortValue: (row) => row.nome,
            },
            {
                id: "acoes",
                header: "AÇÕES",
                sortable: false,
                width: "90px",
                align: "center",
                render: (_, row) => (
                    <AcoesCelula>
                        <BotaoAcao
                            type="button"
                            title="Editar categoria"
                            aria-label="Editar categoria"
                            onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(row);
                            }}
                        >
                            <FiEdit2 size={14} />
                        </BotaoAcao>
                        <BotaoAcao
                            $danger
                            type="button"
                            title="Remover categoria"
                            aria-label="Remover categoria"
                            onClick={(e) => {
                                e.stopPropagation();
                                openDeleteModal(row);
                            }}
                        >
                            <FiTrash2 size={14} />
                        </BotaoAcao>
                    </AcoesCelula>
                ),
            },
        ],
        [openEditModal, openDeleteModal]
    );

    return (
        <>
            <Overlay state={true}>
                <Modal size="lg" header close={closeModal} blockOverflow>
                    <Container>
                        <Header>
                            <Titulo>
                                <FiLayers size={20} />
                                CATÁLOGO DE CATEGORIA DE RISCO
                            </Titulo>
                            <BotaoNovo
                                type="button"
                                onClick={() => openModal("modalNovaCategoriaRiscoDesvios")}
                            >
                                <FiPlus size={16} />
                                ADICIONAR
                            </BotaoNovo>
                        </Header>

                        <TableShell>
                            <SortableTable
                                columns={columns}
                                data={categorias}
                                isLoading={loading}
                                loadingText="Carregando categorias..."
                                emptyText="NENHUMA CATEGORIA CADASTRADA"
                                initialSort={{ key: "nome", direction: "asc" }}
                                getRowId={(row) => row.uuid}
                                onRowClick={openTiposRiscoModal}
                                rowCursor="pointer"
                            />
                        </TableShell>
                    </Container>
                </Modal>
                {isModalOpen("modalNovaCategoriaRiscoDesvios") && (
                    <ModalNovaCategoria updateCategorias={getData} />
                )}
                {isModalOpen("modalEditCategoriaRiscoDesvios") && selectedCategoria && (
                    <ModalEditarCategoria
                        categoria={selectedCategoria}
                        updateCategorias={getData}
                    />
                )}
                {isModalOpen("modalDeleteCategoriaRiscoDesvios") && (
                    <ModalDeleteCategoria
                        categoria={selectedCategoria}
                        updateCategorias={getData}
                        closeModal={closeDeleteModal}
                    />
                )}
                {isModalOpen("modalTiposRiscoDesvios") && (
                    <ModalCatalogoTipoRisco
                        categoria={selectedCategoria}
                        fecharModal={closeTiposRiscoModal}
                        updateCategorias={getData}
                    />
                )}
            </Overlay>
        </>
    );
};

export default ModalCatalogoCategRisco;
